import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as axiosConfiguration from '../../configuration/axios-configuration/axios.token-configuration';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from '../../i18n/useTranslation';
import { redirectToLanguageHome } from '../../shared/services/language.service';
import { PROCUREMENT_LOGIN } from '../../shared/constants/breadcrumb.constants';
import {
    getLoginMessageError,
    saveLoginMessageError,
} from '../../shared/services/local-storage/login-message-error.service';
import { getCommonErrorTranslate } from '../../configuration/axios-configuration/axios.erros.interceptor.service';

export interface IProcurementLogin {
    loginUrl: string;
}

const ProcurementLogin: React.FC<IProcurementLogin> = ({ loginUrl }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { t, i18n } = useTranslation('translation');
    const { language } = useParams();

    // The interceptor stores the API's message as `commonErrors.<message>` for the login screen. The API sends a
    // role code (translated) for some failures but a plain sentence for others (e.g. "Authentication token not
    // returned"), which has no translation - fall back to the generic bad-credentials message for those.
    const showTranslatableLoginError = (): void => {
        const storedError = getLoginMessageError();
        if (!storedError || !i18n.exists(storedError)) {
            saveLoginMessageError(getCommonErrorTranslate('badCredentials'));
        }
    };

    // The language segment of the link, only when the app has translations for it. Anything else (e.g. a mistyped
    // code) is dropped from the requests and redirects, so the app adds its own language to the address.
    // Matched case-insensitively (`/EN/...` works like `/en/...`).
    const linkLanguage: string | undefined =
        language && i18n.hasResourceBundle(language.toLowerCase(), 'translation') ? language.toLowerCase() : undefined;

    // The app takes its language from the saved one (`lang`), not from the address, and rewrites the address to match.
    // The language in a procurement link would be lost, so apply it the way the header's language switcher does.
    const applyLinkLanguage = (): void => {
        if (linkLanguage) {
            localStorage.setItem('lang', linkLanguage);
            void i18n.changeLanguage(linkLanguage);
        }
    };

    useEffect(() => {
        // A language segment the app does not know (a mistyped code) would stay in the address as an extra path
        // segment. Drop it first; the login runs once the address no longer carries it.
        if (language && !linkLanguage) {
            navigate({ pathname: `/${loginUrl}`, search: searchParams.toString() }, { replace: true });
            return;
        }

        const login = async () => {
            applyLinkLanguage();
            try {
                // Procurement links in the wild use `userName` (as documented for the API) as well as `username`.
                const getParam = (name: string): string | null => {
                    const key = Array.from(searchParams.keys()).find((k) => k.toLowerCase() === name.toLowerCase());
                    return key ? searchParams.get(key) : null;
                };
                const username = getParam('username');
                const password = getParam('password');
                const hookUrl = getParam('HOOK_URL');

                const isPlenionLogin = loginUrl === PROCUREMENT_LOGIN.PLENION;

                if (!username || !password || (!isPlenionLogin && !hookUrl)) {
                    setErrorMessage(t('procurement.loginUnexpectedError'));
                    return;
                }

                localStorage.removeItem('token');
                axiosConfiguration.setAuthToken();

                const response = await axios.get(linkLanguage ? `${linkLanguage}/${loginUrl}` : loginUrl, {
                    params: {
                        username,
                        password,
                        ...(isPlenionLogin ? {} : { HOOK_URL: hookUrl }),
                    },
                });
                if (response?.data?.token) {
                    localStorage.setItem('token', response.data.token);

                    sessionStorage.setItem('procurementHookUrl', response.data.hookUrl);

                    axiosConfiguration.setAuthToken();

                    redirectToLanguageHome(response.data.language);

                    navigate('/');
                } else {
                    // Invalid credentials: the API answers 401, which the axios error interceptor handles without
                    // rejecting, so no token comes back. Every other failure (403, 5xx, network) ends up in the catch.
                    showTranslatableLoginError();
                    navigate(linkLanguage ? `/${linkLanguage}/login` : '/login', {
                        replace: true,
                        state: { procurementLoginFailed: true, username },
                    });
                }
            } catch {
                setErrorMessage(t('procurement.loginUnexpectedError'));
            }
        };

        login();
    }, [navigate, searchParams]);
    if (errorMessage) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={8}>
                <Alert severity="error">{t(errorMessage)}</Alert>
            </Box>
        );
    }
    return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={8}>
            <CircularProgress size={20} />

            <Typography color="text.secondary">{t('procurement.connectingSession')}</Typography>
        </Box>
    );
};

export default ProcurementLogin;
